import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon, { IconName } from '../Icon';
import AccountMenu from '../AccountMenu/AccountMenu';
import { useTranslation } from '../../i18n';
import { TabType } from '../../hooks/useNavigation';
import { Goal, Tag } from '../../services/goalService';
import { User } from '../../types/auth';
import { useRunningTimerCount } from '../../contexts/TimerContext';
import { todayKey } from '../../utils/zonedTime';
import './SideNav.css';

const TABS: Array<{ tab: TabType; icon: IconName }> = [
  { tab: 'home', icon: 'calendar' },
  { tab: 'goals', icon: 'goal' },
  { tab: 'time', icon: 'clock' },
  { tab: 'progress', icon: 'chart' },
];

interface SideNavProps {
  activeTab: TabType | null;
  onTabChange: (tab: TabType) => void;
  goals: Goal[];
  user: User | null;
  onLogout: () => void;
  onOpenSuggestion: () => void;
}

/** One search hit: a goal, a stage or a task, and where it opens. */
interface SearchHit {
  key: string;
  title: string;
  /** "Goal › Stage" for a task, the goal for a stage. */
  path?: string;
  kind: 'goal' | 'stage' | 'task';
  href: string;
}

/** Up to [limit] goals, stages and tasks whose title holds [query], goals first. */
export function searchGoalTree(goals: Goal[], query: string, locale?: string, limit = 8): SearchHit[] {
  const q = query.trim().toLocaleLowerCase(locale);
  if (!q) return [];
  const has = (text: string) => text.toLocaleLowerCase(locale).includes(q);
  const hits: SearchHit[] = [];
  for (const g of goals) {
    if (has(g.title)) hits.push({ key: `g-${g.id}`, title: g.title, kind: 'goal', href: `#/goals/${g.id}` });
  }
  for (const g of goals) {
    for (const s of g.subGoals ?? []) {
      if (has(s.title)) {
        hits.push({ key: `s-${s.id}`, title: s.title, path: g.title, kind: 'stage', href: `#/subgoals/${s.id}/todos` });
      }
    }
  }
  for (const g of goals) {
    for (const s of g.subGoals ?? []) {
      for (const t of s.todos ?? []) {
        if (has(t.title)) {
          hits.push({
            key: `t-${t.id}`,
            title: t.title,
            path: `${g.title} › ${s.title}`,
            kind: 'task',
            href: `#/subgoals/${s.id}/todos/${t.id}`,
          });
        }
      }
    }
  }
  return hits.slice(0, limit);
}

/** The tags on the user's tasks, by name; the sidebar lists them as Hedefler filters (T-182). */
export function usedTags(goals: Goal[], locale?: string, limit = 8): Tag[] {
  const byId = new Map<string, Tag>();
  for (const g of goals) for (const s of g.subGoals ?? []) for (const td of s.todos ?? []) {
    for (const tag of td.tags ?? []) byId.set(tag.id, tag);
  }
  return Array.from(byId.values())
    .sort((a, b) => a.name.localeCompare(b.name, locale))
    .slice(0, limit);
}

/**
 * The web shell's left column (Faz 23, T-180; w-*.html `.sidenav`): the brand with the
 * account avatar, search (⌘K / Ctrl+K), the four tabs with today's open tasks and a green dot
 * on Zaman while a timer runs, and Ayarlar at the foot.
 */
const SideNav: React.FC<SideNavProps> = ({ activeTab, onTabChange, goals, user, onLogout, onOpenSuggestion }) => {
  const { t, language } = useTranslation();
  const runningCount = useRunningTimerCount();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const today = todayKey();
  const todayCount = useMemo(
    () =>
      goals.reduce(
        (n, g) =>
          n +
          (g.subGoals ?? []).reduce(
            (m, s) => m + (s.todos ?? []).filter((td) => !td.completed && td.dueDate === today).length,
            0
          ),
        0
      ),
    [goals, today]
  );
  const hits = useMemo(() => searchGoalTree(goals, query, language), [goals, query, language]);
  const tags = useMemo(() => usedTags(goals, language), [goals, language]);
  const hash = window.location.hash;

  // ⌘K / Ctrl+K focuses search from anywhere
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const go = (hit: SearchHit) => {
    window.location.hash = hit.href;
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const showResults = open && query.trim() !== '';
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <nav className="sidenav" aria-label={t('navigation.mainNav')}>
      <div className="sidenav__brand">
        <a href="#/home" className="sidenav__name">
          Riselio
        </a>
        <AccountMenu user={user} onLogout={onLogout} onOpenSuggestion={onOpenSuggestion} />
      </div>

      <div className="sidenav__search-wrap">
        <label className="sidenav__search">
          <Icon name="search" aria-hidden="true" />
          <span className="sr-only">{t('navigation.search')}</span>
          <input
            ref={inputRef}
            type="search"
            placeholder={t('navigation.search')}
            value={query}
            role="combobox"
            aria-expanded={showResults}
            aria-controls="sidenav-search-results"
            aria-autocomplete="list"
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => window.setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, Math.max(hits.length - 1, 0)));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              } else if (e.key === 'Enter' && hits[cursor]) {
                e.preventDefault();
                go(hits[cursor]);
              } else if (e.key === 'Escape') {
                setQuery('');
                setOpen(false);
                inputRef.current?.blur();
              }
            }}
          />
          <kbd aria-hidden="true">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </label>
        {showResults && (
          <ul id="sidenav-search-results" className="sidenav__results" role="listbox">
            {hits.length === 0 && <li className="sidenav__no-results">{t('navigation.searchEmpty')}</li>}
            {hits.map((hit, i) => (
              <li
                key={hit.key}
                role="option"
                aria-selected={i === cursor}
                className={`sidenav__hit${i === cursor ? ' is-active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  go(hit);
                }}
              >
                <Icon name={hit.kind === 'goal' ? 'goal' : hit.kind === 'stage' ? 'list' : 'squareCheck'} />
                <span className="sidenav__hit-text">
                  <span className="sidenav__hit-title">{hit.title}</span>
                  {hit.path && <span className="sidenav__hit-path">{hit.path}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sidenav__list">
        {TABS.map(({ tab, icon }) => (
          <a
            key={tab}
            href={`#/${tab}`}
            className="sidenav__item"
            aria-current={activeTab === tab ? 'page' : undefined}
            onClick={(e) => {
              e.preventDefault();
              onTabChange(tab);
            }}
          >
            <Icon name={icon} />
            {t(`navigation.${tab}`)}
            {tab === 'home' && todayCount > 0 && <span className="sidenav__count">{todayCount}</span>}
            {tab === 'time' && runningCount > 0 && (
              <span className="sidenav__live" role="img" aria-label={t('home.continue.runningNow')} />
            )}
          </a>
        ))}
      </div>

      {tags.length > 0 && (
        <div className="sidenav__list sidenav__tags">
          <span className="sidenav__label">{t('tags.label')}</span>
          {tags.map((tag) => {
            const href = `#/goals?tag=${encodeURIComponent(tag.id)}`;
            return (
              <a key={tag.id} href={href} className="sidenav__item sidenav__item--small" aria-current={hash.endsWith(`?tag=${encodeURIComponent(tag.id)}`) ? 'page' : undefined}>
                <span className="sidenav__tag-dot" style={{ background: tag.color }} aria-hidden="true" />
                <bdi>{tag.name}</bdi>
              </a>
            );
          })}
        </div>
      )}

      <a href="#/settings" className="sidenav__foot" aria-current={window.location.hash === '#/settings' ? 'page' : undefined}>
        <Icon name="settings" />
        {t('navigation.settings')}
      </a>
    </nav>
  );
};

export default SideNav;
